import React from 'react';
import Dialog from './Dialog';
import { IconClock } from './dialogIcons';

/*
  **"Still there?", the warning that a session is about to end for want of
  anybody doing anything.**

  Built to the drawing the owner signed off on 5 September 2026 as part of Group
  H, the system states. Until 8 September it was still the hand-built box it had
  always been, sitting inside `AuthContext`: its own corners, its own shadow, its
  own buttons and its own dimming, none of which matched the eighteen dialogs
  beside it. Group H's README named exactly that and nobody had built it,
  **because until 8 September the warning had never appeared for anybody to
  notice.** It was being wiped by Supabase renewing the token, every hour, for
  ever. See the long note above `IDLE_TIMEOUT_MS` in `AuthContext.jsx`.

  **Four things come from the drawing.**

  - **The one dialog shell**, which is rule 34: a card on a desk, a sheet rising
    from the bottom edge below 1024, the kind said by a small tinted square
    rather than by painting the header.
  - **The countdown stays and stays big**, because it is the one number that
    decides what you do, and it goes red under thirty seconds.
  - **The words say what it costs you** rather than reciting a rule. "For
    security, you will be signed out automatically" is why OnHand does it, not
    why anybody should care.
  - **Staying is the green button.** Staying is what keeps you working; signing
    out now is the one that costs you something, so it is the quiet one. The
    first draft of the drawing had these the other way round and it was
    corrected before he saw it.

  **The one departure from the drawing is the length of time, and it is his.**
  The drawing says 58 minutes and an hour, because that is what the app did on
  the day it was drawn. He asked for two hours on 8 September 2026, so the
  sentence follows the number: words that disagree with what the app does are
  worse than words nobody approved.

  It is its own file so it can be photographed. `AuthContext` decides WHEN it
  appears; this decides what it looks like, and neither knows the other's job.
*/

/*
  Always minutes and seconds, never "45s". The drawing shows 2:00 in tabular
  figures, and a number that changes shape as it passes a minute reads as
  something going wrong rather than as a clock running down.
*/
export function formatCountdown(secs) {
  const safe = Math.max(Number(secs) || 0, 0);
  return Math.floor(safe / 60) + ':' + String(safe % 60).padStart(2, '0');
}

export default function IdleWarning({ secondsLeft, idleFor, onStay, onSignOut }) {
  return (
    <Dialog
      kind="question"
      icon={<IconClock />}
      title="Still there?"
      says="Nothing you have saved is at risk."
      size="question"
      labelledBy="idle-warning-title"
      /* Pressing Escape, the cross, or the dimmed page is a person being there,
         so all three mean "stay", never "sign me out". */
      onClose={onStay}
      footer={
        <>
          <button className="btn btn-quiet" onClick={onSignOut}>Sign out now</button>
          <button className="btn btn-approve" onClick={onStay}>Stay signed in</button>
        </>
      }
    >
      <div style={{ textAlign: 'center' }}>
        <div style={{
          fontSize: 44, fontWeight: 800, lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
          color: secondsLeft <= 30 ? 'var(--critical-ink)' : 'var(--warning-ink)',
          transition: 'color 0.3s',
        }}>
          {formatCountdown(secondsLeft)}
        </div>
        <div style={{
          fontSize: 10.5, fontWeight: 800, letterSpacing: '0.7px',
          textTransform: 'uppercase', color: 'var(--text-secondary)', marginTop: 6,
        }}>
          Until you are signed out
        </div>
      </div>
      <div style={{ height: 16 }} />
      <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-secondary)', lineHeight: 1.62, textAlign: 'center' }}>
        Nothing has been touched for almost {idleFor}. OnHand signs you out on its own
        after {idleFor}, so nobody can walk up to your screen and be you.
      </div>
    </Dialog>
  );
}
